import { useSyncExternalStore } from 'react';

/**
 * The service worker's state as the UI sees it (docs/offline.md). Written by src/pwa/serviceWorker.ts (lazy, only
 * where a worker registers); read by the update notice and the VersionBadge. Tiny on purpose: it sits in the
 * initial bundle.
 */
export interface ServiceWorkerUiState {
    /** A new version is installed and waiting: show "New version available -- Reload". */
    needRefresh: boolean;
    /** The local viewer is precached: this device works offline from here on. */
    offlineReady: boolean;
}

let state: ServiceWorkerUiState = { needRefresh: false, offlineReady: false };
let applyUpdate: (() => void) | null = null;
let checkForUpdate: (() => Promise<boolean>) | null = null;
const listeners = new Set<() => void>();

function set(next: Partial<ServiceWorkerUiState>): void {
    state = { ...state, ...next };
    for (const l of listeners) l();
}

export function subscribeServiceWorker(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getServiceWorkerState(): ServiceWorkerUiState {
    return state;
}

/** A waiting worker: `apply` activates it and reloads the tab (never called without the person's tap). */
export function setNeedRefresh(apply: () => void): void {
    applyUpdate = apply;
    set({ needRefresh: true });
}

export function setOfflineReady(): void {
    set({ offlineReady: true });
}

/** How to ask the registration for a newer worker (returns true when one is now waiting). */
export function setUpdateCheck(check: () => Promise<boolean>): void {
    checkForUpdate = check;
}

/**
 * Reload into the newest build. With a waiting worker: activate it, then reload (a plain reload would come back
 * from the OLD worker's cache). With a worker but nothing waiting yet (the server has a new build the worker has not
 * fetched): check for it first. Without a worker: a plain reload.
 */
export async function reloadToLatest(reload: () => void = () => window.location.reload()): Promise<void> {
    if (!applyUpdate && checkForUpdate) {
        try { await checkForUpdate(); } catch { /* offline or no update: reload as is */ }
    }
    if (applyUpdate) {
        applyUpdate();
        return;
    }
    reload();
}

export function useServiceWorkerState(): ServiceWorkerUiState {
    return useSyncExternalStore(subscribeServiceWorker, getServiceWorkerState, getServiceWorkerState);
}

/** Tests only: back to the initial state. */
export function resetServiceWorkerStateForTests(): void {
    state = { needRefresh: false, offlineReady: false };
    applyUpdate = null;
    checkForUpdate = null;
    for (const l of listeners) l();
}
